/***
*
*   RBAC SETTINGS
*   Manage roles, permissions, and user assignments
*
**********/

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, Tabs, Table, Button, Space, Typography, Tag, Modal,
  Form, Input, Switch, Select, message,
  Tooltip, Badge, Empty, Popconfirm
} from 'antd';
import {
  SettingOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  UserOutlined, LockOutlined, ReloadOutlined, SaveOutlined,
  CheckCircleOutlined, CloseCircleOutlined, TeamOutlined
} from '@ant-design/icons';
import {
  fetchRoles,
  fetchAssignments,
  fetchRBACConfig,
  createRole,
  updateRole,
  deleteRole,
  assignRole,
  removeRoleAssignment,
  invalidateRBACCache,
  fetchMyPermissions
} from '../store/slices/rbacSlice';
import { fetchUsers } from '../store/slices/usersSlice';
import { Animate, usePermissions } from 'components/lib';

const { Title, Text } = Typography;
const { Option } = Select;
const { confirm } = Modal;

const ACTION_LABELS = {
  view: 'View',
  edit: 'Edit',
  delete: 'Delete'
};

export function RBACSettings() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('roles');
  const [editingRole, setEditingRole] = useState(null);
  const [isNewRole, setIsNewRole] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignForm] = Form.useForm();
  const { can } = usePermissions();

  const rbacState = useSelector((state) => state.rbac);
  const usersState = useSelector((state) => state.users);
  const { roles, assignments, config, configLoading, mutationLoading } = rbacState;
  const { data: usersData } = usersState;

  useEffect(() => {
    dispatch(fetchRoles());
    dispatch(fetchAssignments());
    dispatch(fetchRBACConfig());
    dispatch(fetchUsers());
  }, [dispatch]);

  const menuMap = config?.menu_map || {};
  const menuKeys = Object.keys(menuMap);

  const handleRefresh = () => {
    dispatch(invalidateRBACCache());
    dispatch(fetchRoles());
    dispatch(fetchAssignments());
    dispatch(fetchRBACConfig());
    dispatch(fetchMyPermissions());
  };

  // --- Role Handlers ---
  const handleCreateRole = () => {
    const emptyPermissions = {};
    menuKeys.forEach(key => {
      emptyPermissions[key] = { view: false, edit: false, delete: false };
    });
    setEditingRole({
      key: '',
      name: '',
      description: '',
      is_system: false,
      permissions: emptyPermissions
    });
    setIsNewRole(true);
  };

  const handleEditRole = (role) => {
    setEditingRole({ ...role });
    setIsNewRole(false);
  };

  const handleSaveRole = async () => {
    if (!editingRole.name) {
      return message.error('Role name is required');
    }

    try {
      if (isNewRole) {
        if (!editingRole.key) {
          return message.error('Role key is required');
        }
        await dispatch(createRole({
          key: editingRole.key,
          name: editingRole.name,
          description: editingRole.description,
          permissions: editingRole.permissions
        })).unwrap();
        message.success('Role created');
      } else {
        await dispatch(updateRole({
          roleKey: editingRole.key,
          data: {
            name: editingRole.name,
            description: editingRole.description,
            permissions: editingRole.permissions
          }
        })).unwrap();
        message.success('Role updated');
      }
      setEditingRole(null);
      setIsNewRole(false);
      dispatch(fetchRoles());
    } catch (err) {
      message.error(typeof err === 'string' ? err : 'Failed to save role');
    }
  };

  const handleDeleteRole = (roleKey) => {
    confirm({
      title: 'Delete Role',
      content: 'Are you sure? Users assigned to this role will lose their permissions.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await dispatch(deleteRole(roleKey)).unwrap();
          message.success('Role deleted');
          dispatch(fetchRoles());
        } catch (err) {
          message.error(typeof err === 'string' ? err : 'Failed to delete role');
        }
      }
    });
  };

  const togglePermission = (menuKey, action) => {
    setEditingRole(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [menuKey]: {
          ...prev.permissions[menuKey],
          [action]: !prev.permissions[menuKey]?.[action]
        }
      }
    }));
  };

  // --- Assignment Handlers ---
  const handleAssignRole = async () => {
    try {
      const values = await assignForm.validateFields();
      await dispatch(assignRole(values)).unwrap();
      message.success('Role assigned');
      setAssignModalVisible(false);
      assignForm.resetFields();
      dispatch(fetchAssignments());
    } catch (err) {
      if (err?.errorFields) return; // validation error
      message.error(typeof err === 'string' ? err : 'Failed to assign role');
    }
  };

  const handleRemoveAssignment = async (userId) => {
    try {
      await dispatch(removeRoleAssignment(userId)).unwrap();
      message.success('Assignment removed');
      dispatch(fetchAssignments());
    } catch (err) {
      message.error('Failed to remove assignment');
    }
  };

  // --- Roles Table ---
  const rolesColumns = [
    {
      title: 'Role',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <Text strong>{name}</Text>
          {record.is_system && <Tag color="blue">System</Tag>}
        </Space>
      )
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (key) => <Text code>{key}</Text>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Menus Accessible',
      key: 'menus',
      render: (_, record) => {
        const viewable = menuKeys.filter(k => record.permissions?.[k]?.view);
        return <Badge count={viewable.length} style={{ backgroundColor: '#7c3aed' }} />;
      }
    },
    {
      ...(can('settings', 'edit') || can('settings', 'delete')) && {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space>
            {can('settings', 'edit') && (
              <Tooltip title="Edit Permissions">
                <Button type="text" icon={<EditOutlined />} onClick={() => handleEditRole(record)} />
              </Tooltip>
            )}
            {!record.is_system && can('settings', 'delete') && (
              <Tooltip title="Delete">
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteRole(record.key)} />
              </Tooltip>
            )}
          </Space>
        )
      }
    }
  ];

  // --- Assignments Table ---
  const assignmentsColumns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const user = usersData?.users?.find(u => (u._id || u.id) === record.user_id);
        return user ? (
          <Space>
            <UserOutlined />
            <div>
              <Text strong>{`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>{user.email}</Text>
            </div>
          </Space>
        ) : (
          <Text type="secondary">{record.user_id}</Text>
        );
      }
    },
    {
      title: 'Role',
      dataIndex: 'role_name',
      key: 'role_name',
      render: (name, record) => <Tag color="purple">{name || record.role_key}</Tag>
    },
    {
      ...(can('settings', 'edit') || can('settings', 'delete')) && {
        title: 'Actions',
        key: 'actions',
        render: (_, record) => (
          <Space>
            {can('settings', 'edit') && (
              <Tooltip title="Change Role">
                <Button type="text" icon={<EditOutlined />} onClick={() => {
                  assignForm.setFieldsValue({ user_id: record.user_id, role_key: record.role_key });
                  setAssignModalVisible(true);
                }} />
              </Tooltip>
            )}
            {can('settings', 'delete') && (
              <Popconfirm
                title="Remove role assignment?"
                description="User will fall back to default owner role"
                onConfirm={() => handleRemoveAssignment(record.user_id)}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          </Space>
        )
      }
    }
  ];

  // --- Permission Editor ---
  const renderPermissionEditor = () => {
    if (!editingRole) return null;

    return (
      <Modal
        title={isNewRole ? 'Create New Role' : `Edit Role: ${editingRole.name}`}
        open={!!editingRole}
        onCancel={() => { setEditingRole(null); setIsNewRole(false); }}
        onOk={handleSaveRole}
        confirmLoading={mutationLoading}
        width={800}
        okText={<><SaveOutlined /> Save</>}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {isNewRole && (
            <Input
              placeholder="Role key (e.g. content_manager)"
              value={editingRole.key}
              onChange={(e) => setEditingRole(prev => ({ ...prev, key: e.target.value }))}
              addonBefore="Key"
            />
          )}
          <Input
            placeholder="Role name"
            value={editingRole.name}
            onChange={(e) => setEditingRole(prev => ({ ...prev, name: e.target.value }))}
            addonBefore="Name"
          />
          <Input
            placeholder="Description"
            value={editingRole.description}
            onChange={(e) => setEditingRole(prev => ({ ...prev, description: e.target.value }))}
            addonBefore="Description"
          />

          <Card title={<><LockOutlined /> Menu Permissions</>} size="small">
            <Table
              dataSource={menuKeys.map(key => ({
                key,
                menu: menuMap[key]?.label || key,
                ...editingRole.permissions?.[key]
              }))}
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Menu',
                  dataIndex: 'menu',
                  key: 'menu',
                  render: (text) => <Text strong>{text}</Text>
                },
                ...Object.keys(ACTION_LABELS).map(action => ({
                  title: ACTION_LABELS[action],
                  dataIndex: action,
                  key: action,
                  align: 'center',
                  render: (value, record) => (
                    <Switch
                      checked={!!value}
                      onChange={() => togglePermission(record.key, action)}
                      checkedChildren={<CheckCircleOutlined />}
                      unCheckedChildren={<CloseCircleOutlined />}
                      size="small"
                    />
                  )
                }))
              ]}
            />
          </Card>
        </Space>
      </Modal>
    );
  };

  const tabItems = [
    {
      key: 'roles',
      label: (
        <span>
          <LockOutlined /> Roles
          <Badge count={roles.length} style={{ marginLeft: 8, backgroundColor: '#7c3aed' }} showZero />
        </span>
      ),
      children: (
        <Card
          title="Manage Roles"
          extra={
            can('settings', 'edit') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateRole}>
                Create Role
              </Button>
            )
          }
        >
          <Table
            columns={rolesColumns}
            dataSource={roles.map(r => ({ ...r, key: r.key }))}
            pagination={false}
            loading={configLoading}
            locale={{ emptyText: <Empty description="No roles configured" /> }}
          />
          {/* Expanded role detail */}
          {roles.map(role => (
            <Card
              key={role.key}
              title={<><Tag color="purple">{role.name}</Tag> Permission Matrix</>}
              size="small"
              style={{ marginTop: 16 }}
              collapsible="header"
              defaultActiveKey={[]}
            >
              <Table
                dataSource={menuKeys.map(mk => ({
                  key: mk,
                  menu: menuMap[mk]?.label || mk,
                  view: role.permissions?.[mk]?.view,
                  edit: role.permissions?.[mk]?.edit,
                  delete: role.permissions?.[mk]?.delete
                }))}
                pagination={false}
                size="small"
                columns={[
                  { title: 'Menu', dataIndex: 'menu', key: 'menu' },
                  {
                    title: 'View', dataIndex: 'view', key: 'view', align: 'center',
                    render: (v) => v ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  },
                  {
                    title: 'Edit', dataIndex: 'edit', key: 'edit', align: 'center',
                    render: (v) => v ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  },
                  {
                    title: 'Delete', dataIndex: 'delete', key: 'delete', align: 'center',
                    render: (v) => v ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  }
                ]}
              />
            </Card>
          ))}
        </Card>
      )
    },
    {
      key: 'assignments',
      label: (
        <span>
          <TeamOutlined /> User Assignments
          <Badge count={assignments.length} style={{ marginLeft: 8, backgroundColor: '#7c3aed' }} showZero />
        </span>
      ),
      children: (
        <Card
          title="User Role Assignments"
          extra={
            can('settings', 'edit') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setAssignModalVisible(true)}>
                Assign Role
              </Button>
            )
          }
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Users without an explicit assignment default to the <Tag color="green">Owner</Tag> role.
          </Text>
          <Table
            columns={assignmentsColumns}
            dataSource={assignments.map(a => ({ ...a, key: a.user_id }))}
            pagination={{ pageSize: 25 }}
            loading={configLoading}
            locale={{ emptyText: <Empty description="All users have default owner permissions" /> }}
          />
        </Card>
      )
    }
  ];

  return (
    <Animate type="pop">
      <div>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <SettingOutlined style={{ marginRight: 8 }} />
              Roles & Permissions
            </Title>
            <Text type="secondary">Manage access control for Mission Control users</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />

        {renderPermissionEditor()}

        {/* Assign Role Modal */}
        <Modal
          title="Assign Role to User"
          open={assignModalVisible}
          onCancel={() => { setAssignModalVisible(false); assignForm.resetFields(); }}
          onOk={handleAssignRole}
          confirmLoading={mutationLoading}
        >
          <Form form={assignForm} layout="vertical">
            <Form.Item
              name="user_id"
              label="User"
              rules={[{ required: true, message: 'Please select a user' }]}
            >
              <Select
                placeholder="Select a user"
                showSearch
                optionFilterProp="children"
              >
                {usersData?.users
                  ?.filter(user =>
                    user.workspaces?.some(ws => ws.account_type === 'master')
                  )
                  .map(user => (
                    <Option key={user._id || user.id} value={(user._id || user.id)?.toString()}>
                      {`${user.first_name || ''} ${user.last_name || ''}`.trim()} ({user.email})
                    </Option>
                  ))
                }
              </Select>
            </Form.Item>
            <Form.Item
              name="role_key"
              label="Role"
              rules={[{ required: true, message: 'Please select a role' }]}
            >
              <Select placeholder="Select a role">
                {roles.map(role => (
                  <Option key={role.key} value={role.key}>
                    {role.name} {role.is_system && '(System)'}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Animate>
  );
}